/**
 * @file ContentListing/component.js
 */

import { memo } from "react";
import PropTypes from "prop-types";

import ColumnPolicy from "../ContentArea/library/ColumnPolicy";
import MobilePolicy from "../ContentArea/library/MobilePolicy";
import ContentList from "../ContentList/component";
import { sanitizeLinkTracking } from "../../utils/analytics/library";
import { Heading, SectionHeading } from "../Heading/component";

/**
 * Mapping function for returning popular lists
 *
 * @param {string} viewport the current viewport
 * @returns {function} an inner mapping function
 */
function getPopularLists(viewport, headingType, useShortTitle) {
    /**
     * Inner return function
     *
     * @param {object} sectionItem the section item being mapped over
     * @param {number} index the array map index
     * @returns {jsx} jsx markup for the content column
     */
    return function (sectionItem) {
        const { title, shortTitle, link, list } = sectionItem;
        const columnPolicy =
            viewport === "viewportSmall"
                ? new MobilePolicy()
                : new ColumnPolicy();

        const text = useShortTitle && shortTitle ? shortTitle : title;

        const moreProps = {
            text: text,
            link: link,
            external: false,
        };

        const titleElement = title ? (
            <headingType.type {...headingType.props} {...moreProps} />
        ) : null;

        return (
            <div
                className="moreStories sclt-morenews"
                key={`morenews-${title}`}
            >
                <section className="moreStoriesList">
                    {titleElement}
                    <ContentList
                        key={`news-hierarchy-${title}`}
                        displayPolicy={columnPolicy}
                        items={list}
                        loadMore={false}
                        trackingSection={`contentlistmore${sanitizeLinkTracking(
                            link
                        )}`}
                    />
                </section>
            </div>
        );
    };
}

/**
 * ContentListing component
 *
 * @param {string} [title=""] the title to display for the lane
 * @param {array} [contentList=[]] the content to display
 * @param {string} [viewport="viewportLarge"] the current devide viewport
 * @param {bool} [rightRailStyling=false] adjust rendering for right rail style
 */
export const ContentListing = ({
    title = "",
    contentList = [],
    viewport = "viewportLarge",
    rightRailStyling = false,
    useShortTitle = false,
}) => {
    const headingLevel = rightRailStyling ? (
        <Heading text="" level="h2" levelClassName="h3" />
    ) : (
        <SectionHeading text="" />
    );

    return contentList && contentList.length > 0 ? (
        <div className="contentListing" data-testid="content-listing">
            {title && <SectionHeading text={title} />}
            <div className="contentListingContentListHolder">
                {contentList.map(
                    getPopularLists(viewport, headingLevel, useShortTitle)
                )}
            </div>
        </div>
    ) : null;
};

ContentListing.propTypes = {
    title: PropTypes.string,
    contentList: PropTypes.array,
    viewport: PropTypes.string,
    rightRailStyling: PropTypes.bool,
    useShortTitle: PropTypes.bool,
};

export default memo(ContentListing);
